import { Transition } from '@headlessui/react'
import React, { ReactElement, ReactNode, useEffect, useState } from 'react'
import { FeedbackBox, FeedbackVariant } from '../feedback-box'

export interface Toast {
  variant: FeedbackVariant
  text?: string
  hideCloseButton?: boolean
  icon?: ReactNode
  onClose?: () => void
}

export function ToastContainer(props: { toast?: Toast; onClosed?: () => void }): ReactElement | null {
  return (
    <div role="alert">
      {props.toast && (
        <div className="fixed z-1002 inset-4 sm:inset-12 top-auto! pointer-events-none flex justify-center">
          <Content toast={props.toast} onClosed={props.onClosed} />
        </div>
      )}
    </div>
  )
}

function Content(props: { toast: Toast; onClosed?: () => void }) {
  const [show, setShow] = useState(true)
  const hideCloseButton =
    props.toast.variant === 'success-light' || props.toast.variant === 'success-dark' || props.toast.hideCloseButton

  // Reset the show state when a new toast arrives.
  useEffect(() => {
    setShow(true)
  }, [props.toast])

  //the useRef Hook allows you to persist data between renders

  useEffect(() => {
    if (props.onClosed && !show) props.onClosed()
  }, [show])

  // Success toasts auto-hide after a short delay. All other toasts must be closed manually.
  // Loading toasts are expected to be closed by a follow-up success or error toast.
  useEffect(() => {
    if (hideCloseButton) {
      const timeout = setTimeout(setShow.bind(null, false), 3000)
      return () => clearTimeout(timeout)
    }
  }, [props.toast])

  // Add a close button to the toast if the toast contains text.
  const onClose = props.toast.text
    ? (event: React.SyntheticEvent) => {
        // Call stopPropagation so that closing the toast won't trigger a click
        // on the modal overlay behind it (if the toast was opened from a modal).
        event.stopPropagation()
        setShow(false)
        props.toast.onClose && props.toast.onClose()
      }
    : undefined

  return (
    <Transition
      show={show}
      enter="transition-opacity"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="shadow-light pointer-events-auto">
        <FeedbackBox
          text={props.toast.text}
          variant={props.toast.variant}
          icon={props.toast.icon}
          onClose={onClose}
          hideCloseButton={hideCloseButton}
        />
      </div>
    </Transition>
  )
}
